import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Zap, Plus, Search, Download, Upload, Camera, X, Save,
  Send, FileText, Eye, Trash2, Copy, Loader2, ChevronLeft,
  CheckCircle2, AlertTriangle, FileSpreadsheet, Menu, Image as ImageIcon,
  LayoutDashboard, ClipboardList, FolderInput, Settings2 } from "lucide-react";
import { dtApi, dtFileUrl } from "@/lib/dtApi";
import { http } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { useTheme } from "@/lib/theme";
import {
  DRAFT_KEY, newLocalId, compressImage, extractExifGPS, currentLocation,
  blobStore, stripPhoto, isPhotoReady,
} from "@/lib/dtPhotoUpload";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "new", label: "New Inspection", icon: Plus },
  { key: "list", label: "All Inspections", icon: ClipboardList },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "import-data", label: "Import Data", icon: FolderInput },
  { key: "import-photos", label: "Import Photos", icon: ImageIcon },
  { key: "master", label: "Master Data", icon: Settings2 },
];

async function downloadInspectionPdf(inspectionId) {
  try {
    const response = await http.get(
      `/api/dt/inspections/${encodeURIComponent(inspectionId)}/pdf`,
      { responseType: "blob" }
    );

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `DT-${inspectionId}.pdf`;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Inspection PDF download failed:", error);
    toast.error(
      error?.response?.data?.detail ||
      "Could not download inspection PDF"
    );
  }
}


export default function DtInspectionPage() {
  const location = useLocation();
  const initialTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (TABS.some(x => x.key === t)) return t;
    return hasDraftContent(readDraft()) ? "new" : "dashboard";
  }, [location.search]);
  const [tab, setTab] = useState(initialTab);
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appNav, setAppNav] = useState(false);
  const { theme, toggle } = useTheme();

  const openEditor = (id) => { setEditingId(id); setTab("new"); };
  const pickTab = (k) => { setTab(k); setEditingId(null); setDrawerOpen(false); };

  return (
    <div data-testid="dt-layout" className="min-h-screen flex bg-background text-foreground">
      <Sidebar theme={theme} onToggleTheme={toggle} activeRoute="dt-inspection" />

      {appNav && (
        <div data-testid="dt-app-nav-overlay" className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[280px] max-w-[85vw] bg-background border-r border-border">
            <Sidebar mobileOverlay theme={theme} onToggleTheme={toggle} activeRoute="dt-inspection" onClose={() => setAppNav(false)} />
          </div>
          <div className="flex-1 bg-background/60" onClick={() => setAppNav(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
          <button onClick={() => setAppNav(true)} data-testid="dt-app-nav-toggle" aria-label="Open app menu" className="lg:hidden w-9 h-9 rounded-lg bg-muted inline-flex items-center justify-center shrink-0">
            <Menu className="w-5 h-5" />
          </button>
          <button onClick={() => navigate(-1)} data-testid="dt-back" className="hidden sm:inline-flex w-9 h-9 rounded-lg hover:bg-muted inline-flex items-center justify-center shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-lg bg-foreground text-background inline-flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Module</div>
            <div className="font-heading text-sm sm:text-lg font-black leading-tight truncate">DT Inspection</div>
          </div>
          <button onClick={() => { setEditingId(null); setTab("new"); }} data-testid="dt-quick-new" className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-foreground text-background text-xs font-bold shrink-0">
            <Plus className="w-4 h-4" /> New
          </button>
          <button onClick={() => setDrawerOpen(true)} data-testid="dt-mobile-menu" className="sm:hidden w-9 h-9 rounded-lg hover:bg-muted inline-flex items-center justify-center shrink-0" aria-label="Open module menu">
            <ClipboardList className="w-5 h-5" />
          </button>
        </div>
        {/* Tabs — horizontally scrollable on mobile */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pb-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 min-w-max">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} data-testid={`dt-tab-${t.key}`} onClick={() => pickTab(t.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${active ? "bg-foreground text-background" : "border border-border hover:bg-muted"}`}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile drawer with all module tabs */}
      {drawerOpen && (
        <div data-testid="dt-mobile-drawer" className="sm:hidden fixed inset-0 z-50 flex" onClick={() => setDrawerOpen(false)}>
          <div className="ml-auto w-72 max-w-[85vw] bg-background border-l border-border flex flex-col h-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="text-sm font-bold">DT Inspection Menu</div>
              <button onClick={() => setDrawerOpen(false)} className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center" aria-label="Close menu">
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2 space-y-1">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => pickTab(t.key)}
                    className={`w-full inline-flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-left ${active ? "bg-foreground text-background" : "hover:bg-muted"}`}>
                    <Icon className="w-4 h-4 shrink-0" /> {t.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5">
        {tab === "dashboard" && <DashboardTab go={setTab} />}
        {tab === "new" && <InspectionForm key={editingId || "new"} id={editingId} onDone={() => { setEditingId(null); setTab("list"); }} onCancel={() => setTab("list")} />}
        {tab === "list" && <InspectionList onOpen={openEditor} />}
        {tab === "drafts" && <InspectionList onOpen={openEditor} filterStatus="draft" title="Draft Inspections" />}
        {tab === "import-data" && <ImportDataTab />}
        {tab === "import-photos" && <ImportPhotosTab />}
        {tab === "master" && <MasterTab />}
      </main>
      </div>
      <Toaster position="bottom-center" offset={72} theme={theme}
        toastOptions={{ style: { background: "hsl(var(--card))", color: "hsl(var(--foreground))", border: "1px solid hsl(var(--border))" } }} />
    </div>
  );
}

/* ============ DASHBOARD ============ */
function DashboardTab({ go }) {
  const [s, setS] = useState(null);
  useEffect(() => { dtApi.summary().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="text-center py-10 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const finalTiles = Object.entries(s.by_final_status);
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">DT Inspection Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Live statistics from all inspections in the system.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Total Inspections" value={s.total} testid="dt-total" />
        <Tile label="Today" value={s.today} testid="dt-today" />
        <Tile label="This Month" value={s.this_month} testid="dt-month" />
        <Tile label="Draft" value={s.draft} tint="text-amber-600" />
        <Tile label="Submitted" value={s.submitted} tint="text-emerald-600" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">By Final Status</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {finalTiles.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
              <div className="font-heading text-2xl font-black mt-1 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2">
        <button onClick={() => go("new")} data-testid="dt-goto-new" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-foreground text-background text-sm font-bold"><Plus className="w-4 h-4" />New Inspection</button>
        <button onClick={() => go("import-data")} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-border text-sm font-semibold"><FolderInput className="w-4 h-4" />Import Old Data</button>
        <button onClick={() => go("list")} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-border text-sm font-semibold"><ClipboardList className="w-4 h-4" />All Inspections</button>
      </div>
    </div>
  );
}
function Tile({ label, value, tint, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
      <div className={`font-heading text-3xl font-black tabular-nums mt-1 ${tint || ""}`}>{value}</div>
    </div>
  );
}

/* ============ INSPECTION FORM (new / edit) ============ */
const EMPTY_FORM = () => ({
  inspection_date: new Date().toISOString().slice(0, 10),
  circle: "", division: "", sub_division: "", substation: "", feeder: "",
  dt_meter_serial: "", technician_name: "", dt_capacity: "",
  visitor_name: "", dt_location: "", used_cable_size: "", used_cable_length: "",
  dt_condition: null, dt_identification: null, cable_condition: null,
  cable_replacement: null, site_condition: null, safety_condition: null,
  overall_status: null,
  observations: "", final_status: [], remarks: "",
  photos: [], status: "draft",
  client_request_id: newLocalId(),
});

function readDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
}

const DRAFT_FIELDS = ["circle", "division", "sub_division", "substation", "feeder", "dt_meter_serial",
  "technician_name", "dt_capacity", "visitor_name", "dt_location", "used_cable_size", "used_cable_length",
  "dt_condition", "dt_identification", "cable_condition", "cable_replacement", "site_condition",
  "safety_condition", "overall_status", "observations", "remarks"];
function hasDraftContent(d) {
  if (!d) return false;
  return (d.photos || []).length > 0 || (d.final_status || []).length > 0 || DRAFT_FIELDS.some(k => d[k]);
}

function InspectionForm({ id, onDone, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [master, setMaster] = useState({});
  const [constants, setConstants] = useState(null);
  const [f, setF] = useState(() => {
    if (id) return EMPTY_FORM();
    const d = readDraft();
    if (!d) return EMPTY_FORM();
    // In-flight uploads from a previous session are re-queued (server dedupes by client_upload_id).
    d.photos = (d.photos || []).map(p => (p.local_id && p.status !== "uploaded" ? { ...p, status: "pending", progress: 0 } : p));
    return { ...EMPTY_FORM(), ...d };
  });
  const [existing, setExisting] = useState(null);
  const restoredRef = useRef(!id && hasDraftContent(readDraft()));

  useEffect(() => {
    dtApi.master().then(setMaster);
    dtApi.constants().then(setConstants);
    if (restoredRef.current) toast.success("Unsaved inspection restored", { id: "dt-draft-restored" });
  }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dtApi.get(id).then(d => { setExisting(d); setF(d); }).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => {
    if (id) return;
    if (!hasDraftContent(f)) { localStorage.removeItem(DRAFT_KEY); return; }
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(f)); } catch { /* quota */ }
  }, [f, id]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setPhotos = (upd) => setF(s => ({ ...s, photos: typeof upd === "function" ? upd(s.photos) : upd }));
  const setNested = (obj, k, v) => setF(s => ({ ...s, [obj]: { ...s[obj], [k]: v } }));
  const toggleFinal = (opt) => setF(s => ({
    ...s, final_status: s.final_status.includes(opt) ? s.final_status.filter(x => x !== opt) : [...s.final_status, opt]
  }));

  const clearDraft = () => {
    f.photos.forEach(p => p.local_id && blobStore.del(p.local_id));
    localStorage.removeItem(DRAFT_KEY);
  };

  const cancel = () => {
    if (!id && f.photos.length + (f.dt_meter_serial ? 1 : 0) > 0 &&
      !window.confirm("Discard this unsaved inspection and its photos?")) return;
    if (!id) clearDraft();
    onCancel();
  };

  const save = async (asSubmit) => {
    if (saving) return;
    if (asSubmit && !f.dt_meter_serial) { toast.error("DT Meter Serial No. is required to submit"); return; }
    const notReady = f.photos.filter(p => !isPhotoReady(p));
    if (notReady.length) {
      const failed = notReady.filter(p => p.status === "failed").length;
      toast.error(failed
        ? `${failed} photo(s) failed to upload. Tap Retry on them before saving.`
        : `Please wait — ${notReady.length} photo(s) still uploading.`);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, photos: f.photos.map(stripPhoto), status: asSubmit ? "submitted" : "draft" };
      if (id) {
        delete payload.client_request_id;
        await dtApi.update(id, payload);
        toast.success(asSubmit ? "Inspection submitted" : "Draft updated");
      } else {
        const created = await dtApi.create(payload);
        clearDraft();
        toast.success(`Saved as ${created.inspection_no} · ${asSubmit ? "submitted" : "draft"}`);
      }
      onDone();
    } catch (e) {
      const offline = !e?.response;
      toast.error(offline
        ? "Network problem — nothing lost. Your form and photos are saved on this device; tap Submit again when signal returns."
        : (e?.response?.data?.detail || "Save failed"));
    } finally { setSaving(false); }
  };

  if (loading || !constants) return <div className="text-center py-10 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            {id ? "Edit Inspection" : "New DT Inspection & Site Verification"}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
            {existing ? existing.inspection_no : "Draft — auto number on save"}
          </h1>
        </div>
        <div className="text-xs text-right">
          <div className="text-muted-foreground">Inspection Date</div>
          <input type="date" data-testid="dt-date" value={f.inspection_date} onChange={e => set("inspection_date", e.target.value)}
            className="h-9 px-2 rounded-lg border border-border bg-background text-sm" />
        </div>
      </div>

      <Section title="1. Inspection Particulars">
        <FormGrid>
          <Field label="Circle"><DataListInput testid="dt-circle" value={f.circle} onChange={v => set("circle", v)} options={master.circle} /></Field>
          <Field label="Division"><DataListInput value={f.division} onChange={v => set("division", v)} options={master.division} /></Field>
          <Field label="Sub-Division"><DataListInput value={f.sub_division} onChange={v => set("sub_division", v)} options={master.sub_division} /></Field>
          <Field label="Substation"><DataListInput value={f.substation} onChange={v => set("substation", v)} options={master.substation} /></Field>
          <Field label="Feeder"><DataListInput value={f.feeder} onChange={v => set("feeder", v)} options={master.feeder} /></Field>
          <Field label="DT Meter Serial No. *"><Input testid="dt-meter-serial" value={f.dt_meter_serial} onChange={v => set("dt_meter_serial", v)} /></Field>
          <Field label="Technician Name"><DataListInput value={f.technician_name} onChange={v => set("technician_name", v)} options={master.technician} /></Field>
          <Field label="DT Capacity"><DataListInput value={f.dt_capacity} onChange={v => set("dt_capacity", v)} options={master.dt_capacity} /></Field>
          <Field label="Visitor / Inspector"><DataListInput value={f.visitor_name} onChange={v => set("visitor_name", v)} options={master.visitor} /></Field>
          <Field label="Used Cable Size"><DataListInput value={f.used_cable_size} onChange={v => set("used_cable_size", v)} options={master.cable_size} /></Field>
          <Field label="Used Cable Length"><Input value={f.used_cable_length} onChange={v => set("used_cable_length", v)} placeholder="e.g. 45 m" /></Field>
          <Field label="DT Location" full><Input value={f.dt_location} onChange={v => set("dt_location", v)} /></Field>
        </FormGrid>
      </Section>

      <Section title="2. DT & Cable Physical Verification">
        <div className="space-y-3">
          <RadioRow label="DT Condition" value={f.dt_condition} options={constants.dt_condition} onChange={v => set("dt_condition", v)} />
          <RadioRow label="DT Identification" value={f.dt_identification} options={constants.dt_identification} onChange={v => set("dt_identification", v)} />
          <RadioRow label="Cable Condition" value={f.cable_condition} options={constants.cable_condition} onChange={v => set("cable_condition", v)} />
          <RadioRow label="Cable Replacement" value={f.cable_replacement} options={constants.cable_replacement} onChange={v => set("cable_replacement", v)} />
          <RadioRow label="Site Condition" value={f.site_condition} options={constants.site_condition} onChange={v => set("site_condition", v)} />
          <RadioRow label="Safety Condition" value={f.safety_condition} options={constants.safety_condition} onChange={v => set("safety_condition", v)} />
          <RadioRow label="Overall Status" value={f.overall_status} options={constants.overall_status} onChange={v => set("overall_status", v)} />
        </div>
      </Section>

      <Section title="3. Inspection Observation / Findings">
        <textarea data-testid="dt-observations" value={f.observations} onChange={e => set("observations", e.target.value)} rows={4}
          className="w-full rounded-lg border border-border bg-background p-3 text-sm" placeholder="Detailed site observations…" />
      </Section>

      <Section title="4. Final Inspection Status">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {constants.final_status_options.map(opt => (
            <label key={opt} className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-muted">
              <input type="checkbox" checked={f.final_status.includes(opt)} onChange={() => toggleFinal(opt)} data-testid={`dt-final-${opt.slice(0,10)}`} />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="5. Remarks">
        <textarea data-testid="dt-remarks" value={f.remarks} onChange={e => set("remarks", e.target.value)} rows={3}
          className="w-full rounded-lg border border-border bg-background p-3 text-sm" />
      </Section>

      <Section title="6. Photographs">
        <PhotoManager photos={f.photos} setPhotos={setPhotos} />
      </Section>

      {/* Sticky action bar */}
      <div data-testid="dt-action-bar" className="fixed bottom-0 left-0 lg:left-[280px] right-0 z-20 bg-card border-t border-border p-2 sm:p-3 flex gap-2 justify-end flex-wrap safe-area-inset-bottom">
        <button onClick={cancel} data-testid="dt-cancel" className="px-3 sm:px-4 h-10 rounded-full border border-border text-xs sm:text-sm font-semibold">Cancel</button>
        <button onClick={() => save(false)} disabled={saving} data-testid="dt-save-draft" className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-full border border-border text-xs sm:text-sm font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
        </button>
        <button onClick={() => save(true)} disabled={saving} data-testid="dt-submit" className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-full bg-foreground text-background text-xs sm:text-sm font-bold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-border bg-card">
    <div className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{title}</div>
    <div className="p-4">{children}</div>
  </div>
);
const FormGrid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-1">{label}</div>
    {children}
  </div>
);
const Input = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input data-testid={testid} type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" />
);
function DataListInput({ value, onChange, options = [], testid }) {
  const listId = useMemo(() => "dl-" + Math.random().toString(36).slice(2), []);
  return (
    <>
      <input data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)} list={listId}
        className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" />
      <datalist id={listId}>{(options || []).map(o => <option key={o} value={o} />)}</datalist>
    </>
  );
}
function RadioRow({ label, value, options, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="text-xs font-semibold min-w-[130px]">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button key={opt} type="button" onClick={() => onChange(value === opt ? null : opt)} data-testid={`radio-${label}-${opt}`}
            className={`px-3 h-8 rounded-full text-xs font-semibold border ${value === opt ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
function PartyCard({ title, party, onChange, showDate }) {
  const inp = useRef();
  const uploadSig = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const u = await dtApi.uploadFile(f, { ref_type: "inspection-sig" });
      onChange("signature_url", u.url);
      toast.success("Signature uploaded");
    } catch { toast.error("Upload failed"); }
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
      <Input value={party.name} onChange={v => onChange("name", v)} placeholder="Name" />
      {showDate
        ? <Input value={party.date} onChange={v => onChange("date", v)} placeholder="Date" type="date" />
        : <Input value={party.mobile} onChange={v => onChange("mobile", v)} placeholder="Mobile" />}
      <div className="flex items-center gap-2">
        {party.signature_url ? <img src={dtFileUrl(party.signature_url)} className="h-10 w-24 object-contain bg-muted rounded" alt="sig" /> : null}
        <label className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-border text-xs font-semibold cursor-pointer hover:bg-muted">
          <Upload className="w-3.5 h-3.5" />Signature
          <input ref={inp} type="file" accept="image/*" onChange={uploadSig} className="hidden" />
        </label>
        {party.signature_url && <button onClick={() => onChange("signature_url", "")} className="text-xs text-muted-foreground">Clear</button>}
      </div>
    </div>
  );
}

/* Photo manager with camera + gallery + category */
const NEW_PHOTO_CATEGORIES = [
  "DT Image",
  "DT Meter Image with Serial No.",
  "Installed Cable Image",
  "Complete Site Image",
];

const RETRY_DELAYS = [2000, 5000];
const STATUS_STYLE = {
  pending: "bg-muted text-muted-foreground",
  uploading: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  uploaded: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  failed: "bg-red-500/15 text-red-700 dark:text-red-300",
};
const STATUS_LABEL = { pending: "Pending", uploading: "Uploading", uploaded: "Uploaded", failed: "Failed" };

const fmtKb = (n) => (n ? `${Math.round(n / 1024)} KB` : "");

// Writes upload result straight into the saved draft so it survives the form being unmounted mid-upload.
function persistUploaded(localId, done) {
  const d = readDraft();
  if (!d?.photos?.some(p => p.local_id === localId)) return;
  d.photos = d.photos.map(p => (p.local_id === localId ? { ...p, ...done } : p));
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* quota */ }
}

function usePhotoUploadQueue(photos, setPhotos) {
  const blobs = useRef(new Map());
  const active = useRef(null);
  const [previews, setPreviews] = useState({});

  const patch = (localId, changes) =>
    setPhotos(list => list.map(p => (p.local_id === localId ? { ...p, ...changes } : p)));

  const getBlob = async (localId) => {
    if (!blobs.current.has(localId)) {
      const b = await blobStore.get(localId);
      if (b) blobs.current.set(localId, b);
    }
    return blobs.current.get(localId);
  };

  // Build thumbnails for photos that are not yet on the server (incl. restored drafts).
  useEffect(() => {
    photos.filter(p => p.local_id && p.status !== "uploaded" && !previews[p.local_id]).forEach(async p => {
      const b = await getBlob(p.local_id);
      if (b) setPreviews(v => ({ ...v, [p.local_id]: URL.createObjectURL(b) }));
      else patch(p.local_id, { status: "failed", error: "Photo file missing on this device — please re-take" });
    });
  }, [photos]);

  const runUpload = async (p) => {
    active.current = p.local_id;
    patch(p.local_id, { status: "uploading", progress: 0, error: null });
    const blob = await getBlob(p.local_id);
    if (!blob) {
      patch(p.local_id, { status: "failed", error: "Photo file missing on this device — please re-take" });
      active.current = null;
      return;
    }
    const file = blob instanceof File ? blob : new File([blob], p.filename || "photo.jpg", { type: blob.type || "image/jpeg" });
    for (let attempt = 0; ; attempt++) {
      try {
        const up = await dtApi.uploadFile(file, { ref_type: "inspection", ref_id: p.category, client_upload_id: p.local_id },
          { onProgress: (pct) => patch(p.local_id, { progress: Math.min(pct, 99) }) });
        const done = { status: "uploaded", progress: 100, file_id: up.id, url: up.url, uploaded_at: new Date().toISOString() };
        patch(p.local_id, done);
        persistUploaded(p.local_id, done);
        blobStore.del(p.local_id);
        break;
      } catch (e) {
        const retryable = !e?.response || e.response.status >= 500 || e.response.status === 408;
        if (retryable && attempt < RETRY_DELAYS.length && navigator.onLine) {
          await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
          continue;
        }
        patch(p.local_id, { status: "failed", progress: 0, error: e?.response?.data?.detail || (navigator.onLine ? "Upload failed" : "No network") });
        break;
      }
    }
    active.current = null;
    setPhotos(list => [...list]);
  };

  // Sequential queue: one photo at a time so weak connections are not overwhelmed.
  useEffect(() => {
    if (active.current) return;
    const next = photos.find(p => p.local_id && p.status === "pending");
    if (next) runUpload(next);
  }, [photos]);

  // When network comes back, automatically re-queue failed photos.
  useEffect(() => {
    const onOnline = () => setPhotos(list => list.map(p => (p.status === "failed" && blobs.current.has(p.local_id) ? { ...p, status: "pending" } : p)));
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [setPhotos]);

  const addFile = async (file, category) => {
    const localId = newLocalId();
    const [exif, here] = await Promise.all([extractExifGPS(file), currentLocation()]);
    let compressed = file;
    try { compressed = await compressImage(file); } catch { /* upload original if browser cannot decode */ }
    blobs.current.set(localId, compressed);
    await blobStore.put(localId, compressed);
    setPreviews(v => ({ ...v, [localId]: URL.createObjectURL(compressed) }));
    const gps = exif || (here && { ...here, source: "device" }) || {};
    setPhotos(list => [...list, {
      local_id: localId, category, filename: compressed.name || file.name, status: "pending", progress: 0,
      original_size: file.size, size: compressed.size,
      latitude: gps.latitude || null, longitude: gps.longitude || null, gps_source: gps.source || null,
    }]);
    if (!gps.latitude) toast(`${category}: no GPS location captured — enable location services and retake if possible.`);
  };

  const retry = (localId) => patch(localId, { status: "pending", error: null });
  const remove = (p) => {
    if (p.local_id) { blobStore.del(p.local_id); blobs.current.delete(p.local_id); }
    setPhotos(list => list.filter(x => (p.local_id ? x.local_id !== p.local_id : x.file_id !== p.file_id)));
  };
  const srcOf = (p) => (p.url ? dtFileUrl(p.url) : previews[p.local_id] || "");

  return { addFile, retry, remove, srcOf };
}

function UploadSummary({ photos }) {
  const tracked = photos.filter(p => p.local_id);
  if (!tracked.length) return null;
  const done = tracked.filter(p => p.status === "uploaded").length;
  const failed = tracked.filter(p => p.status === "failed").length;
  const pct = Math.round(tracked.reduce((a, p) => a + (p.status === "uploaded" ? 100 : p.progress || 0), 0) / tracked.length);
  return (
    <div data-testid="dt-upload-summary" className="mb-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between text-xs font-semibold">
        <span data-testid="dt-upload-summary-text">{done} of {tracked.length} photos uploaded{failed ? ` · ${failed} failed` : ""}</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full transition-[width] duration-300 ${failed ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PhotoManager({ photos, setPhotos }) {
  const q = usePhotoUploadQueue(photos, setPhotos);
  const [busy, setBusy] = useState(false);
  const onPick = async (file, cat) => {
    setBusy(true);
    try { await q.addFile(file, cat); } catch { toast.error("Could not read this photo"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <UploadSummary photos={photos} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {NEW_PHOTO_CATEGORIES.map(cat => (
          <PhotoColumn key={cat} title={cat}
            photos={photos.filter(p => p.category === cat)}
            onUpload={(f) => onPick(f, cat)}
            onRemove={q.remove} onRetry={q.retry} srcOf={q.srcOf}
            busy={busy}
          />
        ))}
      </div>
      {photos.some(p => !NEW_PHOTO_CATEGORIES.includes(p.category)) && (
        <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Additional / legacy photos (kept for historical records)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {photos.filter(p => !NEW_PHOTO_CATEGORIES.includes(p.category)).map(p => (
              <div key={p.file_id} className="rounded-lg border border-border overflow-hidden bg-muted">
                <img src={dtFileUrl(p.url)} alt={p.filename} className="w-full h-24 object-cover" />
                <div className="p-1 flex items-center justify-between text-[10px]">
                  <span className="text-muted-foreground truncate">{p.category || "Other"}</span>
                  <button onClick={() => q.remove(p)} className="text-red-600"><Trash2 className="w-3 h-3"/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");

function PhotoStatus({ p, onRetry, testid }) {
  if (!p.local_id) return null;
  return (
    <div className="mt-1 space-y-1" data-testid={`${testid}-status`}>
      <div className="flex items-center justify-between gap-1">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[p.status]}`}>
          {STATUS_LABEL[p.status]}{p.status === "uploading" ? ` ${p.progress || 0}%` : ""}
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{fmtKb(p.size)}</span>
        {p.status === "failed" && (
          <button data-testid={`${testid}-retry`} onClick={() => onRetry(p.local_id)} className="px-2 h-6 rounded-full border border-border text-[10px] font-bold hover:bg-muted">Retry</button>
        )}
      </div>
      {p.status === "uploading" && (
        <div className="h-1 rounded-full bg-muted overflow-hidden"><div className="h-full bg-sky-500 transition-[width]" style={{ width: `${p.progress || 0}%` }} /></div>
      )}
      {p.status === "failed" && p.error && <div className="text-[10px] text-red-600">{p.error}</div>}
    </div>
  );
}

function PhotoColumn({ title, photos, onUpload, onRemove, onRetry, srcOf, busy }) {
  const camRef = useRef(); const galRef = useRef();
  const first = photos[0];
  const tid = `dt-photo-${slug(title)}`;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={tid}>
      <div className="px-3 py-2 border-b border-border bg-muted/40">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
      </div>
      <div className="p-3 space-y-2">
        {first ? (
          <div className="relative group">
            <img src={srcOf(first)} alt={first.filename} className="w-full h-40 object-cover rounded-lg bg-muted" />
            <button onClick={() => onRemove(first)} data-testid={`${tid}-remove`} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600" title="Remove">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <PhotoStatus p={first} onRetry={onRetry} testid={tid} />
            {first.latitude ? (
              <div className="mt-1 text-[10px] font-mono text-muted-foreground text-center">
                Lat {first.latitude.toFixed(6)} · Lon {first.longitude.toFixed(6)}
                {first.gps_source && <span className="ml-1 opacity-60">({first.gps_source})</span>}
              </div>
            ) : (
              <div className="mt-1 text-[10px] text-amber-700 text-center">No GPS captured</div>
            )}
          </div>
        ) : (
          <div className="h-40 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center text-muted-foreground gap-1">
            <ImageIcon className="w-8 h-8 opacity-40" />
            <div className="text-[11px]">{busy ? "Compressing…" : "No photo"}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-1">
          <button onClick={() => camRef.current?.click()} disabled={busy} data-testid={`${tid}-camera`} className="h-8 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center justify-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Camera
          </button>
          <button onClick={() => galRef.current?.click()} disabled={busy} data-testid={`${tid}-gallery`} className="h-8 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center justify-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" /> Gallery
          </button>
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        <input ref={galRef} type="file" accept="image/*" className="hidden" data-testid={`${tid}-input`}
          onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        {photos.length > 1 && (
          <div className="grid grid-cols-3 gap-1">
            {photos.slice(1).map(p => (
              <div key={p.local_id || p.file_id} className="relative">
                <img src={srcOf(p)} alt="" className="w-full h-14 object-cover rounded bg-muted" />
                <button onClick={() => onRemove(p)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"><Trash2 className="w-2.5 h-2.5"/></button>
                {p.local_id && p.status !== "uploaded" && (
                  <button onClick={() => p.status === "failed" && onRetry(p.local_id)} className={`absolute bottom-0.5 left-0.5 px-1 rounded text-[9px] font-bold ${STATUS_STYLE[p.status]}`}>
                    {p.status === "failed" ? "Retry" : STATUS_LABEL[p.status]}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ LIST + FILTERS + PAGINATION ============ */
function InspectionList({ onOpen, filterStatus, title }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(filterStatus || "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [master, setMaster] = useState({});
  const [division, setDivision] = useState("");

  useEffect(() => { dtApi.master().then(setMaster); }, []);
  useEffect(() => { setPage(1); }, [q, status, startDate, endDate, division]);
  useEffect(() => {
    setLoading(true);
    dtApi.list({ q, status, start_date: startDate, end_date: endDate, division, page, page_size: 25 })
      .then(d => { setRows(d.items); setTotal(d.total); setPages(d.pages); })
      .finally(() => setLoading(false));
  }, [q, status, startDate, endDate, division, page]);

  const del = async (id, no) => {
    if (!window.confirm(`Delete inspection ${no}?`)) return;
    await dtApi.remove(id); toast.success("Deleted");
    setRows(rows.filter(r => r.id !== id));
  };
  const dup = async (id) => {
    const nd = await dtApi.duplicate(id);
    toast.success(`Duplicated as ${nd.inspection_no}`);
    onOpen(nd.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight flex-1">
          {title || "All Inspections"}
        </h1>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="dt-search" placeholder="Search Inspection#, DT serial, technician…" className="w-full h-10 pl-9 pr-3 rounded-lg bg-background border border-border text-sm" />
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap gap-2 items-center">
        {!filterStatus && (
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="dt-status-filter" className="h-9 px-2 rounded-lg border border-border bg-background text-xs">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
          </select>
        )}
        <select value={division} onChange={e => setDivision(e.target.value)} className="h-9 px-2 rounded-lg border border-border bg-background text-xs">
          <option value="">All divisions</option>
          {(master.division || []).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 px-2 rounded-lg border border-border bg-background text-xs" />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 px-2 rounded-lg border border-border bg-background text-xs" />
        <a href={dtApi.exportUrl({ start_date: startDate, end_date: endDate })} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-xs font-semibold hover:bg-muted" data-testid="dt-export-excel">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Export Excel
        </a>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        <span className="text-xs text-muted-foreground ml-auto">{total} record{total !== 1 ? "s" : ""}</span>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <Th>Inspection #</Th><Th>Date</Th><Th>DT Meter Serial</Th><Th>Substation</Th>
              <Th>Technician</Th><Th>Overall</Th><Th>Status</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground text-xs">{loading ? "Loading…" : "No records"}</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                <td className="px-3 py-2 font-mono text-xs">{r.inspection_no}</td>
                <td className="px-3 py-2 text-xs">{r.inspection_date}</td>
                <td className="px-3 py-2 text-xs">{r.dt_meter_serial || r.dt_serial || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.substation || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.technician_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.overall_status || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${r.status === "submitted" ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => onOpen(r.id)} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted" data-testid={`dt-edit-${r.id}`}>Edit</button>
                  <button onClick={() => downloadInspectionPdf(r.id)} className="text-xs px-2 py-1 rounded border border-blue-500/40 text-blue-700 hover:bg-blue-50 ml-1" data-testid={`dt-pdf-${r.id}`}>PDF</button>
                  <button onClick={() => dup(r.id)} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted ml-1" title="Duplicate"><Copy className="w-3.5 h-3.5 inline" /></button>
                  <button onClick={() => del(r.id, r.inspection_no)} className="text-xs px-2 py-1 rounded border border-border hover:bg-red-50 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map(r => (
          <div key={r.id} className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-sm font-bold">{r.inspection_no}</div>
                <div className="text-[11px] text-muted-foreground">{r.inspection_date} · {r.dt_meter_serial || r.dt_serial || "—"}</div>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${r.status === "submitted" ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>{r.status}</span>
            </div>
            <div className="text-xs mt-2 text-muted-foreground truncate">{r.substation || "—"} · {r.technician_name || "—"}</div>
            <div className="mt-2 grid grid-cols-4 gap-1">
              <button onClick={() => onOpen(r.id)} className="h-8 rounded-lg border border-border text-xs">Edit</button>
              <button onClick={() => downloadInspectionPdf(r.id)} className="h-8 rounded-lg border border-blue-500/40 text-blue-700 text-xs inline-flex items-center justify-center">PDF</button>
              <button onClick={() => dup(r.id)} className="h-8 rounded-lg border border-border text-xs">Dup</button>
              <button onClick={() => del(r.id, r.inspection_no)} className="h-8 rounded-lg border border-border text-xs text-red-600">Del</button>
            </div>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-1">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="h-8 px-3 rounded border border-border disabled:opacity-40">Prev</button>
          <span className="text-xs text-muted-foreground px-2">Page {page} of {pages}</span>
          <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="h-8 px-3 rounded border border-border disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
const Th = ({ children, className = "" }) => <th className={`px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground ${className}`}>{children}</th>;

/* ============ IMPORT DATA (Excel) ============ */
function ImportDataTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => { dtApi.importHistory().then(d => setHistory(d.items || [])); }, [result]);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try { setPreview(await dtApi.importPreview(file)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const doCommit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await dtApi.importCommit({ records: preview.records, skip_duplicates: true });
      setResult(r); setPreview(null); setFile(null);
      toast.success(`${r.imported} imported`);
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Import Old Data</h1>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <a href={dtApi.templateUrl()} className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border border-border text-sm font-semibold hover:bg-muted" data-testid="dt-tmpl-dl">
            <Download className="w-4 h-4" /> Download Template
          </a>
          <label className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border border-border text-sm font-semibold cursor-pointer hover:bg-muted" data-testid="dt-file-pick">
            <Upload className="w-4 h-4" /> {file ? file.name : "Choose Excel"}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => { setFile(e.target.files?.[0]); setPreview(null); setResult(null); }} />
          </label>
          <button onClick={doPreview} disabled={!file || busy} data-testid="dt-preview-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-foreground text-background text-sm font-bold disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
          </button>
        </div>
      </div>

      {preview && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-3 justify-between">
            <div className="text-sm">
              <b>{preview.total_rows}</b> rows · {preview.duplicates.length} duplicates · {preview.warnings.length} warnings
            </div>
            <button onClick={doCommit} disabled={busy} data-testid="dt-commit-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-foreground text-background text-sm font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm Import ({preview.total_rows - preview.duplicates.length})
            </button>
          </div>
          {preview.warnings.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs max-h-40 overflow-y-auto">
              {preview.warnings.map((w, i) => <div key={i}>Row {w.row}: {w.issue}</div>)}
            </div>
          )}
          <div className="max-h-80 overflow-auto border border-border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0"><tr><Th>Row</Th><Th>Date</Th><Th>Legacy Visit</Th><Th>DT Serial</Th><Th>Substation</Th><Th>Technician</Th></tr></thead>
              <tbody>
                {preview.records.map((r, i) => {
                  const isDup = preview.duplicates.some(d => d.row === r.row);
                  return (
                    <tr key={i} className={`border-b border-border ${isDup ? "bg-red-50" : ""}`}>
                      <td className="px-2 py-1">{r.row}{isDup && <span className="ml-1 text-red-600 text-[10px] font-bold">DUP</span>}</td>
                      <td className="px-2 py-1">{r.inspection_date || "—"}</td>
                      <td className="px-2 py-1 font-mono">{r.legacy_visit_no || "—"}</td>
                      <td className="px-2 py-1">{r.dt_serial}</td>
                      <td className="px-2 py-1">{r.substation}</td>
                      <td className="px-2 py-1">{r.technician_name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-500/50 bg-emerald-500/5 p-4 text-sm">
          <div className="flex items-center gap-2 font-bold text-emerald-700 mb-2"><CheckCircle2 className="w-4 h-4" /> Import Complete</div>
          <div className="grid grid-cols-3 gap-2">
            <div><b>{result.imported}</b> imported</div>
            <div>{result.skipped} skipped</div>
            <div>{result.failed} failed</div>
          </div>
          {result.imported_numbers?.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              Generated: {result.imported_numbers.slice(0, 10).join(", ")}{result.imported_numbers.length > 10 ? "…" : ""}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        <div className="px-4 py-2 border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground">Recent Imports</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[520px]">
            <thead className="bg-muted/40"><tr><Th>When</Th><Th className="text-right">Provided</Th><Th className="text-right">Imported</Th><Th className="text-right">Skipped</Th><Th className="text-right">Failed</Th></tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted-foreground">No imports yet</td></tr>}
              {history.map(h => (
                <tr key={h.id} className="border-b border-border">
                  <td className="px-3 py-1.5 whitespace-nowrap">{(h.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-1.5 text-right">{h.provided}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-600 font-bold">{h.imported}</td>
                  <td className="px-3 py-1.5 text-right">{h.skipped}</td>
                  <td className="px-3 py-1.5 text-right text-red-600">{h.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============ IMPORT PHOTOS (ZIP) ============ */
function ImportPhotosTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [mapping, setMapping] = useState({});

  useEffect(() => {
    dtApi.unmatchedPhotos().then(d => setUnmatched(d.items || []));
    dtApi.list({ page_size: 200 }).then(d => setInspections(d.items || []));
  }, [result]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try { setResult(await dtApi.importPhotoZip(file)); toast.success("Processed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const assign = async (u) => {
    const iid = mapping[u.id];
    if (!iid) return toast.error("Pick an inspection first");
    await dtApi.assignPhoto({ unmatched_id: u.id, inspection_id: iid, category: "Other" });
    toast.success(`Assigned to ${inspections.find(i => i.id === iid)?.inspection_no}`);
    setUnmatched(unmatched.filter(x => x.id !== u.id));
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Import Old Photos</h1>
      <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border border-border text-sm font-semibold cursor-pointer hover:bg-muted">
          <Upload className="w-4 h-4" /> {file ? file.name : "Choose ZIP"}
          <input type="file" accept=".zip" className="hidden" onChange={e => setFile(e.target.files?.[0])} />
        </label>
        <button onClick={upload} disabled={!file || busy} data-testid="dt-photozip-up" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-foreground text-background text-sm font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload ZIP
        </button>
      </div>
      {result && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Matched" value={result.matched_count} tint="text-emerald-600" />
          <StatTile label="Unmatched" value={result.unmatched_count} tint="text-amber-600" />
          <StatTile label="Duplicates" value={result.duplicate_count} tint="text-red-600" />
          <StatTile label="Missing GPS" value={result.missing_gps_count || 0} tint="text-red-600" />
        </div>
      )}
      {result && result.missing_gps?.length > 0 && (
        <div className="rounded-lg border border-red-500/50 bg-red-500/5 p-3 text-xs">
          <div className="font-bold text-red-700 mb-1">GPS location data missing from these photos — they were NOT imported:</div>
          {result.missing_gps.slice(0, 15).map((m, i) => <div key={i}>• {m.filename}</div>)}
          {result.missing_gps.length > 15 && <div className="text-muted-foreground">…and {result.missing_gps.length - 15} more. Please re-shoot with location services enabled.</div>}
        </div>
      )}
      {unmatched.length > 0 && (
        <div>
          <h2 className="font-heading text-base font-bold mb-2">Unmatched Photos — Assign Manually</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {unmatched.map(u => (
              <div key={u.id} className="rounded-lg border border-border bg-card overflow-hidden">
                <img src={dtFileUrl(u.url)} alt={u.filename} className="w-full h-40 object-cover bg-muted" />
                <div className="p-3 space-y-2">
                  <div className="text-xs font-mono truncate">{u.filename}</div>
                  <select value={mapping[u.id] || ""} onChange={e => setMapping({ ...mapping, [u.id]: e.target.value })} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-xs">
                    <option value="">Pick inspection…</option>
                    {inspections.map(i => <option key={i.id} value={i.id}>{i.inspection_no} · {i.dt_meter_serial || i.dt_serial || "—"}</option>)}
                  </select>
                  <button onClick={() => assign(u)} className="w-full h-9 rounded-full bg-foreground text-background text-xs font-bold">Assign</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function StatTile({ label, value, tint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">{label}</div>
      <div className={`font-heading text-2xl font-black tabular-nums ${tint || ""}`}>{value}</div>
    </div>
  );
}

/* ============ MASTER DATA ============ */
function MasterTab() {
  const [m, setM] = useState({});
  const [busy, setBusy] = useState(null);
  useEffect(() => { dtApi.master().then(setM); }, []);
  const save = async (key, values) => {
    setBusy(key);
    try { const r = await dtApi.updateMaster(key, values); setM({ ...m, [key]: r.values }); toast.success(`${key} saved`); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Master Data</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(m).filter(([k]) => k !== "updated_at").map(([key, values]) => (
          <MasterEditor key={key} name={key} values={values || []} onSave={vs => save(key, vs)} busy={busy === key} />
        ))}
      </div>
    </div>
  );
}
function MasterEditor({ name, values, onSave, busy }) {
  const [rows, setRows] = useState(values);
  useEffect(() => setRows(values), [values]);
  const add = () => setRows([...rows, ""]);
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-wider">{name.replace(/_/g, " ")}</div>
        <button onClick={() => onSave(rows.filter(Boolean))} disabled={busy} className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-foreground text-background text-xs font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {rows.map((v, i) => (
          <div key={i} className="flex gap-1">
            <input value={v} onChange={e => setRows(rows.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 h-8 px-2 rounded border border-border bg-background text-sm" />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="w-8 h-8 rounded border border-border hover:bg-red-50 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Plus className="w-3.5 h-3.5" /> Add value</button>
    </div>
  );
}


